import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import MapaImage from '../../assets/images/HomeContacto/home-contacto-section-mapa.webp';
import HomeContactoCard from './HomeContactoCard';
import styles from './HomeContacto.module.scss';

const HOVER_SCALE = 1.05;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.5 },
  },
};

const mapVariants = {
  hidden: { x: -150, y: -50, scale: 0.8, opacity: 0 },
  visible: {
    x: 0,
    y: 0,
    scale: 1,
    opacity: 1,
    transition: { type: 'spring', stiffness: 60, damping: 15 },
  },
};

const cardVariants = {
  hidden: { x: 150, opacity: 0 },
  visible: {
    x: 0,
    opacity: 1,
    transition: { type: 'spring', stiffness: 60, damping: 15 },
  },
};

const HomeContacto = () => {
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [loading, setLoading] = useState(true);

  // 1) Capturamos el scroll global
  const { scrollY } = useScroll();
  // 2) Lo transformamos a un valor de background-position
  //    Aquí reducimos el movimiento a un 20% del scroll: -y * 0.2 px
  const backgroundPosition = useTransform(
    scrollY,
    y => `center ${-y * 0.2}px`
  );

  useEffect(() => {
    async function fetchOficinas() {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/oficinas?populate=d3map`
        );
        const json = await response.json();

        if (Array.isArray(json.data)) {
          const formatted = json.data.map((oficina) => {
            const coords =
              oficina.d3map && oficina.d3map.length > 0
                ? {
                    latitud: oficina.d3map[0].latitud,
                    longitud: oficina.d3map[0].longitud,
                  }
                : { latitud: null, longitud: null };

            return {
              name: oficina.pais || 'Sin país',
              email: oficina.correo || 'Sin correo',
              direccion: oficina.direccion || 'Sin dirección',
              representante: oficina.representante || 'Sin representante',
              telefono: oficina.telefono || 'Sin teléfono',
              ...coords,
            };
          });

          setCountries(formatted);
          setSelectedCountry(formatted[0] || null);
        } else {
          console.error('Formato inesperado de API:', json);
        }
      } catch (error) {
        console.error('Error al obtener oficinas:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchOficinas();
  }, []);

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
  };

  if (loading) {
    return (
      <motion.div
        className={styles.loading}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        Cargando...
      </motion.div>
    );
  }

  if (!countries.length) {
    return <div className={styles.loading}>No se encontraron oficinas.</div>;
  }

  return (
    <motion.div
      className={styles.container}
      // aplicamos la animación de parallax al background del propio contenedor
      style={{ backgroundPosition }}
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
    >
      {/* ——— MAPA ——— */}
      <motion.div
        className={styles.mapContainer}
        variants={mapVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        <motion.img
          className={styles.map}
          src={MapaImage}
          alt="Mapa de oficinas"
          whileHover={{ scale: HOVER_SCALE }}
          onClick={() => (window.location.href = '/contacto')}
        />
      </motion.div>

      {/* ——— TARJETA DE DATOS ——— */}
      <motion.div
        className={styles.cardContainer}
        variants={cardVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        <HomeContactoCard
          selectedCountry={selectedCountry}
          onCountryChange={handleCountrySelect}
          countries={countries}
        />
      </motion.div>
    </motion.div>
  );
};

export default HomeContacto;
